import {
    Body1,
    Button,
    Field,
    Input,
    InteractionTag,
    InteractionTagPrimary,
    InteractionTagSecondary,
    makeStyles,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    shorthands,
    Spinner,
    Subtitle1,
    Text,
    Textarea,
    tokens,
    Tooltip,
} from '@fluentui/react-components';
import { ArrowUpload24Regular, AttachRegular } from '@fluentui/react-icons';
import { DragEvent, FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { appInsights } from '../../AppInsights';
import { useNotification } from '../../Contexts/NotificationContext';
import { useUserContext } from '../../Contexts/UserContext';
import { useConfirm } from '../../Contexts/hooks';
import { FileParameter, GptConfiguration, IGptConfiguration, StringStringTuple } from '../../EnterpriseGPTApi/EnterpriseGPTApiV1Client';
import useGdriveDownload from '../../Hooks/useGdriveDownload';
import useGooglePicker from '../../Hooks/useGooglePicker';
import { performEnterpriseGPTApiCall } from '../../Hooks/useMiddlewareApiCall';
import { ExploreGptRoute } from '../../Routes';
import { cleanFileName } from '../../Utils/file';
import { addGptConfiguration, editGptConfiguration } from '../../store/actions/gptConfigurationsAction';
import { useFeature, useRootState } from '../../store/hooks';
import { EventName_UserInteraction, EventProperty_Value_ApplicationType, EventProperty_Value_PersonalGptUploadDocument, UserInteractionEventProperties } from '../../store/models/customEvents';
import { FileTypeIcon } from '../Common/FileTypeIcon';
import { LexicalEditor, LexicalEditorRef } from '../Common/LexicalEdit/LexicalEditor';
import SpinnerOverlay from '../Common/SpinnerOverlay';
import './PersonalGptView.scss';

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.padding('50px', '200px'),
        ...shorthands.gap('20px'),
        height: 'auto',
        '@media (max-width: 768px)': {
            ...shorthands.padding('20px', '10px'),
        },
    },
    formContainer: {
        display: 'flex',
        flexDirection: 'column',
        height: 'auto',
        boxShadow: `0 0 10px ${tokens.colorNeutralForeground3}`,
        ...shorthands.borderRadius('0.75rem'),
        ...shorthands.padding('20px', '30px'),
         marginBottom: '20px',
        '@media (max-width: 768px)': {
            ...shorthands.padding('15px', '15px'),
            ...shorthands.borderRadius('0.5rem'),
        },
    },
    formFieldsContainer: {
        display: 'flex',
        flexDirection: 'column',
        height: 'auto',
        ...shorthands.gap('20px'),
        '@media (max-width: 768px)': {
            ...shorthands.gap('15px'),
        },
    },
    instructionTextArea: {
        height: '160px',
        '@media (max-width: 768px)': {
            height: '120px',
        },
    },
    knowledgeContainer: {
        marginTop: '10px',
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        ...shorthands.gap('10px'),
        position: 'relative',
        padding: '10px 20px 20px 20px',
        border: '2px dashed transparent',
        borderRadius: '4px',
        transition: 'all 0.2s ease',
        '@media (max-width: 768px)': {
            padding: '10px 15px 15px 15px',
            ...shorthands.gap('8px'),
        },
    },
    draggingOver: {
        border: `2px dashed ${tokens.colorBrandStroke1}`,
        backgroundColor: tokens.colorBrandBackground2,
    },
    buttonsContainer: {
        display: 'flex',
        flexDirection: 'row-reverse',
        ...shorthands.gap('10px'),
        '@media (max-width: 768px)': {
            flexDirection: 'column-reverse',
            ...shorthands.gap('12px'),
        },
    },
    uploadIconContainer: {
        display: 'flex',
        flexDirection: 'row',
        height: '35px',
        alignItems: 'center',
        ...shorthands.gap('10px'),
    },
    dropOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        borderRadius: '4px',
        zIndex: 10,
    },
    dropMessage: {
        fontSize: '18px', // Increased font size
        fontWeight: 'bold',
        color: tokens.colorBrandForeground1,
        padding: '20px',
        textAlign: 'center',
        borderRadius: '8px',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        '@media (max-width: 768px)': {
            fontSize: '16px',
            padding: '15px',
        },
    },
    filesStatusContainer: {
        marginTop: '10px',
        marginBottom: '15px',
        padding: '12px',
        backgroundColor: tokens.colorNeutralBackground1,
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        borderRadius: '4px',
        fontSize: '14px',       
        gap: 'var(--Components-Alert-Placeholder-Gap-Between, 10px)',
    },
    statusLine: {
        display: 'flex',
        alignItems: 'flex-start',
        marginBottom: '8px',
        '&:last-child': {
            marginBottom: 0,
        },
    },
    partialFilesList: {
        marginTop: '12px',
        marginLeft: '16px',
        color: 'var(--Advanced-Forms-Drag-n-Drop-File-Uploads-Secondary-Text-Color, #9CA3AF)',
        /* SM/Medium */
        fontFamily: 'Inter',
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: '500',
        lineHeight: '20px', /* 142.857% */
        letterSpacing: '0.07px',
    },
    hideButton: {
        width: 'fit-content',
        height: 'fit-content',
        marginLeft: '12px',

        padding: 'var(--Components-Button-Spacing-Y-SM, 10px) var(--Components-Button-Spacing-X-SM, 12px)',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 'var(--Utilities-Spacing-2, 8px)',
        alignSelf: 'stretch',
        borderRadius: 'var(--Components-Button-Border-Radius, 8px)',
        border: '1px solid var(--Components-Button-Outline-Primary-Border-Color, #FF5730)',
        background: 'var(--Components-Button-Outline-Primary-Bg-Focus-Color, rgba(255, 255, 255, 0.01))'
    },
});

const defaultPersonalGpt: IGptConfiguration = {
    id: '',
    name: '',
    description: '',
    prompt: '',
};

const PersonalGptView: FC = () => {
    const styles = useStyles();
    const { t } = useTranslation();
    const { dispatchNotification } = useNotification();
    const { dispatch } = useRootState();
    const navigate = useNavigate();
    const { userProfile } = useUserContext();
    const confirm = useConfirm();
    const chatFeature = useFeature('chat');

    // Helper function to apply attachment configuration fallback for personal GPTs
    const applyAttachmentConfigFallback = (gpt: IGptConfiguration): IGptConfiguration => {
        // If it's a personal GPT without its own attachment configuration, 
        // assign the global personal GPT attachment configuration
        if (gpt.isPersonal && !gpt.attachment && chatFeature.personalGpt?.attachmentConfiguration) {
            return {
                ...gpt,
                attachment: chatFeature.personalGpt.attachmentConfiguration
            };
        }
        return gpt;
    };

    const supportedFileExtensions = chatFeature.personalGpt?.knowledgeBase?.extensions;
    const maxSupportedFileSizeMB = chatFeature.personalGpt?.knowledgeBase?.maxSizeMB;
    const maxSupportedTotalFilesSizeMB = chatFeature.personalGpt?.knowledgeBase?.maxTotalFilesSizeMB;
    const hiddenFileInput = useRef(null as HTMLInputElement | null);
    const [initialFiles, setInitialFiles] = useState<string[]>([]);
    const [fileNames, setFileNames] = useState<string[]>([]);
    const [filesToUpsert, setFilesToUpsert] = useState<FileParameter[]>([]);
    const [filesToDelete, setFilesToDelete] = useState<string[]>([]);
    const [filesStatus, setFilesStatus] = useState<StringStringTuple[]>([]);
    const [showPartialFiles, setShowPartialFiles] = useState<boolean>(false);
    const [personalGpt, setPersonalGpt] = useState<IGptConfiguration | undefined>(undefined);
    // Ref for LexicalEditor
    const instructionEditorRef = useRef<LexicalEditorRef>(null);
    const [mode, setMode] = useState<'create' | 'edit'>('create');
    const [isLoading, setIsLoading] = useState(false);
    const [selectedAction, setSelectedAction] = useState<'create' | 'edit' | 'confirmDelete' | undefined>('create');
    const [isFormFilled, setIsFormFilled] = useState(false);
    const [isDriveFileDownloading, setIsDriveFileDownloading] = useState<boolean>(false);
    const [maxFilesReached, setMaxFilesReached] = useState(false);
    const [isDraggingOver, setIsDraggingOver] = useState(false);
    const { id: personalGptId } = useParams();

    const { getFilesByIds } = useGdriveDownload(confirm, chatFeature.personalGpt?.knowledgeBase?.extensions!);

    const onUploadDocumentAddRef = useRef<(files: FileList) => void>();

    // Handle Google Picker Callback
    const onPickerCallback = async (data: any): Promise<void> => {
        if (data.action === window.google.picker.Action.PICKED) {
            const fileIds = (data.docs as any[]).map((x) => x.id);
            const fileNames = (data.docs as any[]).map((x) => x.name);

            setIsDriveFileDownloading(true);
            const files: FileList | null = await getFilesByIds(fileIds, fileNames);
            setIsDriveFileDownloading(false);

            // after downloading the files, add them to the list
            if (files) {
                onUploadDocumentAddRef.current!(files);
            }
        }
    };

    const { openPicker } = useGooglePicker(onPickerCallback, chatFeature.personalGpt?.knowledgeBase?.extensions?.map((x) => x.mimeType).join(',')!);

    useEffect(() => {
        // if the view is in edit mode, fetch the personal GPT to be edited
        if (personalGptId) {
            setMode('edit');
            performEnterpriseGPTApiCall(async (client) => {
                try {
                    const response = await client.getPersonalGpt(personalGptId);
                    const personalGpt = new GptConfiguration(); // model for GptConfiguration and API response from the plugin is different
                    personalGpt.id = response.id;
                    personalGpt.name = response.name;
                    personalGpt.description = response.description;
                    personalGpt.prompt = response.instruction;
                    setPersonalGpt(personalGpt);
                    setInitialFiles(response.files ?? []);
                    setFileNames(response.files ?? []);
                    setFilesStatus(response.filesStatus ?? []);
                } catch (e) {
                    dispatchNotification('error.api.post.personalGpt', 'error');
                }
            });
        }
        // if it is in view mode, create an empty state
        else {
            setPersonalGpt(defaultPersonalGpt);
        }
    }, []);

    // check mandatory fields
    useEffect(() => {
        const isValid = personalGpt?.name && personalGpt?.description && personalGpt?.prompt ? true : false;
        setIsFormFilled(isValid);
    }, [personalGpt]);

    // Set LexicalEditor content only when loading a new GPT (on mount or when switching GPTs),
    // and only if the prompt is different from the current editor content
    useEffect(() => {
        if (instructionEditorRef.current && personalGpt?.prompt !== undefined) {
            const editorContent = instructionEditorRef.current.getContent ? instructionEditorRef.current.getContent() : undefined;
            if (editorContent !== personalGpt.prompt) {
                instructionEditorRef.current.setContent(personalGpt.prompt);
            }
        }
    }, [personalGptId, mode, personalGpt?.prompt]);

    // Keep LexicalEditor and state in sync
    const handleInstructionChange = (markdown: string) => {
        setPersonalGpt((p) => ({ ...p, prompt: markdown }));
    };

    // when using the local drive, simulate the user click on the hidden file input
    const onUploadFromLocalDriveClick = useCallback(() => {
        if (hiddenFileInput.current) {
            hiddenFileInput.current.click();
            hiddenFileInput.current.value = '';
        }
    }, []);

    // when using Google Drive, open the Google Picker
    const onUploadFromGoogleDriveClick = useCallback(() => {
        const expiresBy = localStorage.getItem('expiresBy');
        if (localStorage.getItem('pc_t') && expiresBy) {
            if (new Date().getTime() >= new Date(expiresBy).getTime()) {
                localStorage.removeItem('pc_t');
                localStorage.removeItem('expiresBy');
            }
        }
        openPicker();
    }, []);

    const onCreatePersonalGpt = useCallback(() => {
        performEnterpriseGPTApiCall(async (client, abortSignal) => {
            try {
                setIsLoading(true);
                setSelectedAction('create');
                // starters parameters is not currently used
                const personalGptResult = await client.createPersonalGpt(personalGpt?.name, personalGpt?.description, personalGpt?.prompt, [], filesToUpsert, [], abortSignal);
                setIsLoading(false);
                dispatch(addGptConfiguration(applyAttachmentConfigFallback(personalGptResult)));
                navigate(ExploreGptRoute);
                dispatchNotification('personalGpt.actions.create', 'success');
            } catch (e) {
                setIsLoading(false);
                dispatchNotification('error.api.post.personalGpt', 'error');
            }
        });
    }, [personalGpt, filesToUpsert]);

    // when editing a personal GPT, the API expects in input both the new files (as binaries) and the files to be deleted (as file names)
    const onEditPersonalGpt = useCallback(() => {
        performEnterpriseGPTApiCall(async (client, abortSignal) => {
            try {
                setIsLoading(true);
                setSelectedAction('edit');
                // starters parameters is not currently used
                const personalGptResult = await client.modifyPersonalGpt(
                    personalGpt?.id!,
                    personalGpt?.name,
                    personalGpt?.description,
                    personalGpt?.prompt,
                    [],
                    filesToUpsert,
                    filesToDelete,
                    abortSignal,
                );
                setIsLoading(false);
                dispatch(editGptConfiguration(applyAttachmentConfigFallback(personalGptResult)));
                navigate(ExploreGptRoute);
                dispatchNotification('personalGpt.actions.edit', 'success');
            } catch (e) {
                setIsLoading(false);
                dispatchNotification('error.api.put.personalGpt', 'error');
            }
        });
    }, [personalGpt, filesToUpsert, filesToDelete]);

    // when a file is uploaded, check if it is valid and add it to the list
    // all the modifications happen locally. Only when submitting the form, the modifications are sent to the API
    const onUploadDocumentAdd = useCallback(
        (files: FileList) => {
            var customEvent = {
                name: EventName_UserInteraction,
                properties: {
                    applicationType: EventProperty_Value_ApplicationType,
                    userId: userProfile?.username,
                    interactionType: EventProperty_Value_PersonalGptUploadDocument,
                } as UserInteractionEventProperties,
            };

            // Check if adding all files would exceed the maximum allowed
            if (fileNames.length + files.length > chatFeature.personalGpt?.knowledgeBase?.maxFiles!) {
                setMaxFilesReached(true);
                customEvent.properties.isSuccess = false;
                customEvent.properties.error = 'maxFilesReached';
                appInsights.trackEvent(customEvent);

                confirm({
                    show: true,
                    title: t(`application.dialog.warningTitle`),
                    message: `${t(`personalGpt.knowledgeBase.maxFilesReached`)} ${chatFeature.personalGpt?.knowledgeBase?.maxFiles}.`,
                    confirmText: t('Ok'),
                });
                return;
            }

            // Calculate total size of existing files
            const existingTotalSize = filesToUpsert.reduce((total, doc) => total + doc.data.size / 1000000, 0);
            // Calculate total size of new files
            const newFilesTotalSize = Array.from(files).reduce((total, file) => total + file.size / 1000000, 0);

            // Check if total size would exceed the maximum allowed
            if (maxSupportedTotalFilesSizeMB && existingTotalSize + newFilesTotalSize > maxSupportedTotalFilesSizeMB) {
                customEvent.properties.isSuccess = false;
                customEvent.properties.error = 'totalFilesTooLarge';
                appInsights.trackEvent(customEvent);

                confirm({
                    show: true,
                    title: t(`application.dialog.warningTitle`),
                    message: `${t(`personalGpt.knowledgeBase.totalFilesTooLarge`)} ${maxSupportedTotalFilesSizeMB} MB`,
                    confirmText: t('Ok'),
                });
                return;
            }

            // Process files in batch instead of one by one to avoid state update issues
            const newFilesToUpsert: FileParameter[] = [...filesToUpsert];
            const newFileNames: string[] = [...fileNames];
            let validFiles = 0;

            for (let i = 0; i < files.length; i++) {
                const file = files[i];

                // Check file extension
                if (supportedFileExtensions && !supportedFileExtensions?.some((ext) => file.name.toLowerCase().endsWith(ext.value!))) {
                    continue;
                }

                // Check if file is empty
                if (file.size == 0) {
                    continue;
                }

                // Check file size
                if (maxSupportedFileSizeMB && file.size / 1000000 > maxSupportedFileSizeMB) {
                    continue;
                }

                // File is valid, process it
                var cleanedFileName = cleanFileName(file.name);

                // Remove any existing file with the same name
                const fileToUpsertIndex = newFilesToUpsert.findIndex((x) => x.fileName === cleanedFileName);
                if (fileToUpsertIndex !== -1) {
                    newFilesToUpsert.splice(fileToUpsertIndex, 1);
                }

                // Add the new file to upload list
                newFilesToUpsert.push({ fileName: cleanedFileName, data: file });

                // Update the displayed file names
                const fileNameIndex = newFileNames.indexOf(cleanedFileName);
                if (fileNameIndex !== -1) {
                    newFileNames.splice(fileNameIndex, 1);
                }

                newFileNames.push(cleanedFileName);
                validFiles++;
            }

            // Update states once with all changes
            if (validFiles > 0) {
                setFilesToUpsert(newFilesToUpsert);
                setFileNames(newFileNames);

                customEvent.properties.isSuccess = true;
                appInsights.trackEvent(customEvent);
            }
        },
        [fileNames, filesToUpsert],
    );

    useEffect(() => {
        onUploadDocumentAddRef.current = onUploadDocumentAdd;
    }, [onUploadDocumentAdd]);

    // all the modifications happen locally. Only when submitting the form, the modifications are sent to the API
    const onUploadDocumentRemove = useCallback(
        (fileToRemove: string) => {
            // remove file from current list
            setFileNames((f) => f.filter((file) => file !== fileToRemove));
            // remove binary from current list
            setFilesToUpsert((f) => f.filter((file) => file.fileName !== fileToRemove));
            // check if an original file is asked to be deleted
            if (initialFiles.some((x) => x === fileToRemove)) {
                setFilesToDelete((f) => [...f, fileToRemove]);
            }
        },
        [fileNames, filesToUpsert, filesToDelete],
    );

    // Drag and drop event handlers
    const handleDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDraggingOver(true);
    }, []);

    const handleDragLeave = useCallback((event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();

        // Check if we're actually leaving the container and not just entering a child element
        if (event.currentTarget.contains(event.relatedTarget as Node)) {
            return;
        }

        setIsDraggingOver(false);
    }, []);

    const handleDrop = useCallback(
        (event: DragEvent<HTMLDivElement>) => {
            event.preventDefault();
            event.stopPropagation();
            setIsDraggingOver(false);

            if (event.dataTransfer.files && event.dataTransfer.files.length > 0 && !maxFilesReached && !isLoading) {
                // Use the existing method to add files
                onUploadDocumentAdd(event.dataTransfer.files);
            }
        },
        [onUploadDocumentAdd, maxFilesReached, isLoading],
    );

    return (
        <div className={styles.container}>
            <Subtitle1>{t('personalGpt.title')}</Subtitle1>
            {personalGpt && (
                <div className={styles.formContainer}>
                    <div className={styles.formFieldsContainer}>
                        <Field label={t('personalGpt.fields.nameLabel')} size='large'>
                            <Body1>{t('personalGpt.fields.nameDescription')}</Body1>
                            <Input placeholder={t('personalGpt.fields.namePlaceholder')} onChange={(_event, data) => setPersonalGpt((p) => ({ ...p, name: data.value }))} value={personalGpt?.name} />
                        </Field>
                        <Field label={t('personalGpt.fields.descriptionLabel')} size='large'>
                            <Body1>{t('personalGpt.fields.descriptionDescription')}</Body1>
                            <Textarea
                                placeholder={t('personalGpt.fields.descriptionPlaceholder')}
                                onChange={(_event, data) => setPersonalGpt((p) => ({ ...p, description: data.value }))}
                                size='large'
                                value={personalGpt?.description}
                            />
                        </Field>
                        <div style={{ marginBottom: 20 }}>
                            <span
                                id="instruction-editor-label"
                                style={{ 
                                    fontWeight: 600, 
                                    fontSize: 16, 
                                    display: 'block', 
                                    marginBottom: 4 
                                }}
                            >
                                {t('personalGpt.fields.instructionLabel')}
                            </span>
                            <Body1 style={{ marginBottom: 8 }}>{t('personalGpt.fields.instructionDescription')}</Body1>
                            <LexicalEditor
                                id="instruction-editor"
                                ariaLabelledby="instruction-editor-label"
                                ref={instructionEditorRef}
                                className={`${styles.instructionTextArea} lexicalEditorCustom`}
                                placeholder={t('personalGpt.fields.instructionPlaceholder')}
                                onChange={handleInstructionChange}
                            />
                        </div>
                        {chatFeature.personalGpt?.knowledgeBase?.isEnabled && (
                            <Field label={t('personalGpt.fields.knowledgeLabel')} size='large'>
                                <Body1>{t('personalGpt.fields.knowledgeDescription')}</Body1>

                                {/* Files Status Display */}
                                {filesStatus && filesStatus.length > 0 && (
                                    <div className={styles.filesStatusContainer}>
                                        {(() => {
                                            const completeFiles = filesStatus.filter(fs => fs.item2 !== 'Partial');
                                            const partialFiles = filesStatus.filter(fs => fs.item2 === 'Partial');
                                            
                                            return (
                                                <>
                                                    {completeFiles.length > 0 && (
                                                        <div className={styles.statusLine}>
                                                            <img src='/icons/check-check.svg' width='16px' height='16px'/>
                                                            {t('personalGpt.knowledgeBase.nbDocSuccessfullyProcessedLabel', { count: completeFiles.length })}
                                                        </div>
                                                    )}
                                                    {partialFiles.length > 0 && (
                                                        <div className={styles.statusLine}>
                                                            <img src='/icons/file-warning.svg' width='16px' height='16px' />
                                                            {t('personalGpt.knowledgeBase.nbDocPartialProcessedLabel', { count: partialFiles.length })}
                                                            <span
                                                                className={styles.hideButton}
                                                                onClick={() => setShowPartialFiles(!showPartialFiles)}
                                                            >
                                                                {showPartialFiles ?  'hide' : 'show' }
                                                            </span>
                                                            {showPartialFiles && (
                                                                <div className={styles.partialFilesList}>
                                                                    {partialFiles.map((file, index) => (
                                                                        <div key={index}>• {file.item1}</div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </>
                                            );
                                        })()}
                                    </div>
                                )}

                                <div
                                    className={`${styles.knowledgeContainer} ${isDraggingOver ? styles.draggingOver : ''}`}
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={handleDrop}
                                >
                                    <Menu>
                                        <MenuTrigger>
                                            <Tooltip content={!maxFilesReached ? 'Upload a document' : 'You have reached the maximum number of files'} relationship='label'>
                                                <MenuButton icon={<AttachRegular />} disabled={maxFilesReached || isLoading}></MenuButton>
                                            </Tooltip>
                                        </MenuTrigger>
                                        <MenuPopover>
                                            <MenuList>
                                                <MenuItem onClick={onUploadFromLocalDriveClick}>
                                                    <div className={styles.uploadIconContainer}>
                                                        <ArrowUpload24Regular />
                                                        Upload from local files
                                                    </div>
                                                </MenuItem>
                                                <MenuItem onClick={onUploadFromGoogleDriveClick}>
                                                    <div className={styles.uploadIconContainer}>
                                                        <img src='/GoogleDrive-icon.svg' width='24px' />
                                                        Upload from Google Drive
                                                    </div>
                                                </MenuItem>
                                            </MenuList>
                                        </MenuPopover>
                                    </Menu>
                                    <input
                                        type='file'
                                        multiple
                                        accept={supportedFileExtensions?.map((x) => x.value).join(', ')}
                                        ref={hiddenFileInput}
                                        hidden
                                        onChange={(event) => onUploadDocumentAdd(event.target.files!)}
                                    />
                                    {fileNames.length > 0 ? (
                                        <>
                                            {fileNames.map((file, index) => {
                                                return (
                                                    <InteractionTag key={index}>
                                                        <InteractionTagPrimary icon={<FileTypeIcon filename={file} />} hasSecondaryAction>
                                                            {file}
                                                        </InteractionTagPrimary>
                                                        <InteractionTagSecondary aria-label='dismiss' onClick={() => onUploadDocumentRemove(file)} />
                                                    </InteractionTag>
                                                );
                                            })}
                                            {isDriveFileDownloading && (
                                                <InteractionTag>
                                                    <Spinner size='small'></Spinner>
                                                    <InteractionTagPrimary>Uploading...</InteractionTagPrimary>
                                                </InteractionTag>
                                            )}
                                        </>
                                    ) : isDriveFileDownloading ? (
                                        <InteractionTag>
                                            <Spinner size='small'></Spinner>
                                            <InteractionTagPrimary>Uploading...</InteractionTagPrimary>
                                        </InteractionTag>
                                    ) : (
                                        <Text>Drop files here or click to upload</Text>
                                    )}
                                    {isDraggingOver && (
                                        <div className={styles.dropOverlay}>
                                            <div className={styles.dropMessage}>Drop files here to upload</div>
                                        </div>
                                    )}
                                </div>
                            </Field>
                        )}
                    </div>
                    <div className={styles.buttonsContainer}>
                        {mode === 'edit' && (
                            <>
                                <SpinnerOverlay isLoading={isLoading && selectedAction === 'edit'}>
                                    <Button onClick={onEditPersonalGpt} appearance='primary' disabled={isLoading}>
                                        {t('personalGpt.buttons.edit')}
                                    </Button>
                                </SpinnerOverlay>
                            </>
                        )}
                        {mode === 'create' && (
                            <SpinnerOverlay isLoading={isLoading && selectedAction === 'create'}>
                                <Button onClick={onCreatePersonalGpt} appearance='primary' disabled={isLoading || !isFormFilled}>
                                    {t('personalGpt.buttons.create')}
                                </Button>
                            </SpinnerOverlay>
                        )}
                        <Button onClick={() => navigate(ExploreGptRoute)}>{t('personalGpt.buttons.cancel')}</Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PersonalGptView;
